import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import TBody from '../../components/table/TBody/TBody';
import TD from '../../components/table/TD/TD';
import TR from '../../components/table/TR/TR';
import Table from '../../components/table/Table';
import TH from '../../components/table/TH/TH';
import THead from '../../components/table/THead/THead';
import Button from '../../components/form/button/Button';
import Icon from '../../components/icon/Icon';
import TableWrapper from '../components/table-wrapper/TableWrapper';
import { RootState } from '../../core/state/state';
import NoResultsPage from '../../components/no-results-page/NoResultsPage';
import AddCompanyContact from './modals/add-company-contact/AddCompanyContactModal';
import ManageCompanyContact from './modals/manage-company-contact/ManageCompanyContactModal';
import { getExternalUserContacts } from '../../core/state/subcontractors/asyncActions';
import Badge from '../components/badge/Badge';
import { Color } from '../../core/enums/Color';

import './CompanyContacts.scss';

const CompanyContacts: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const contacts = useSelector((state: RootState) => state.subcontractor.contacts);
  const [isManageCompanyContactModalOpen, setIsManageCompanyContactModalOpen] = useState<boolean>(false);
  const [isAddCompanyContactModalOpen, setIsAddCompanyContactModalOpen] = useState<boolean>(false);
  const userID = useSelector((state: RootState) => state.user.userInfo?.userID);

  React.useEffect(() => {
    if (userID) {
      dispatch(getExternalUserContacts(Number(userID)));
    }
  }, [dispatch, userID]);

  return (
    <TableWrapper>
      <div className="table__header">
        <span>{t('company.header.title')}</span>
        <div className="d-flex">
          <Button
            onClick={() => setIsManageCompanyContactModalOpen(true)}
            className="company-contacts__btn-manage-roles"
            buttonTypes={['blue-outline', 'small']}
          >
            <span>{t('company.manageContacts')}</span>
          </Button>
          <Button onClick={() => setIsAddCompanyContactModalOpen(true)} className="company-contacts__btn-add" buttonTypes={['blue', 'small']}>
            <Icon className="company-contacts__icon-plus" icon="plus" size="extra-small" svg />
            {t('common.addNew')}
          </Button>
        </div>
      </div>
      <div className="company-contacts__table">
        <Table>
          <THead>
            <TR>
              <TH>{t('company.heading.nameSurnameName')}</TH>
              <TH>{t('common.companyNumber')}</TH>
            </TR>
          </THead>
          <TBody>
            {contacts?.map((contact) => (
              <TR key={contact.id}>
                <TD className="text-left">
                  <span className="font-weight-bold">{`${contact.firstName === 'Unknown' ? '-' : contact.firstName} ${
                    contact.lastName === 'Unknown' ? '-' : contact.lastName
                  }`}</span>
                </TD>
                <TD className="text-left">{contact.phoneNumber}</TD>
                <TD className="text-left">
                  {contact.isPrimary && <Badge label={t('externalUsers.companyContacts.primaryContact')} color={Color.LightBlue} />}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
        {!contacts && (
          <NoResultsPage
            title={t('common.noUsers')}
            description={t('internalUsers.noResults.description')}
            icon="profile"
            iconClass="company-contacts__no-results-icon"
          />
        )}
      </div>
      {isAddCompanyContactModalOpen && (
        <AddCompanyContact
          isOpen={isAddCompanyContactModalOpen}
          closeModal={() => setIsAddCompanyContactModalOpen(false)}
          notificationSuccessMessage={t('company.modal.addContact.form.message.success')}
        />
      )}
      {isManageCompanyContactModalOpen && contacts && (
        <ManageCompanyContact isOpen={isManageCompanyContactModalOpen} closeModal={() => setIsManageCompanyContactModalOpen(false)} />
      )}
    </TableWrapper>
  );
};

export default CompanyContacts;
